import {
  Box,
  TextField,
  Grid,
  MenuItem,
  Typography,
  FormHelperText,
  IconButton,
  Tooltip,
} from "@mui/material";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import { useUsers } from "../../../../../../Hooks/useUsers";

const FormFields = ({
  formData,
  handleInputChange,
  handleFileChange,
  errors,
  isSubmitting = false,
}) => {
  const { userRoles } = useUsers();

  const handleRemovePhoto = () => {
    // Clear the profilePic from formData
    handleInputChange({
      target: {
        name: "profilePic",
        value: null,
      },
    });
  };

  return (
    <>
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: { xs: "center", sm: "flex-start" },
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ width: "100%", mb: 2 }}>
          Employee Photo
        </Typography>
        <Box
          sx={{
            width: 100,
            height: 100,
            border: errors.profilePic
              ? "1px solid #d32f2f"
              : formData.profilePic
              ? "1px solid #e0e0e0"
              : "1px solid #cccccc",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            position: "relative",
            backgroundColor: "#FAFAFA",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "action.hover",
              transform: "scale(1.02)",
            },
          }}
          onClick={() =>
            !formData.profilePic &&
            document.getElementById("profilePicInput")?.click()
          }
          aria-label={
            formData.profilePic ? "Profile photo" : "Upload profile picture"
          }
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              !formData.profilePic &&
                document.getElementById("profilePicInput")?.click();
            }
          }}
        >
          {formData.profilePic ? (
            <>
              <img
                src={
                  formData.profilePic instanceof File
                    ? URL.createObjectURL(formData.profilePic)
                    : formData.profilePic
                }
                alt="Profile Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0,
                  transition: "opacity 0.2s ease-in-out",
                  "&:hover": { opacity: 1 },
                  borderRadius: "4px",
                }}
              >
                <Tooltip title="Remove photo">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemovePhoto();
                    }}
                    sx={{
                      color: "white",
                      backgroundColor: "rgba(244, 67, 54, 0.8)",
                      "&:hover": {
                        backgroundColor: "rgba(244, 67, 54, 1)",
                      },
                      width: 32,
                      height: 32,
                    }}
                    size="small"
                    disabled={isSubmitting}
                  >
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          ) : (
            <CameraAltOutlinedIcon
              color="action"
              sx={{
                fontSize: 32,
                color: "#666",
              }}
            />
          )}
          <input
            accept="image/*"
            id="profilePicInput"
            type="file"
            hidden
            onChange={handleFileChange}
            disabled={isSubmitting}
          />
        </Box>
        <Typography
          color="secondary"
          variant="caption"
          sx={{
            mt: 1,
            fontStyle: "italic",
            textAlign: "center",
            fontSize: "0.75rem",
          }}
        >
          {formData.profilePic
            ? "Click the delete icon to remove photo"
            : "Click to upload a profile picture"}
        </Typography>
        {errors.profilePic && (
          <Typography color="error" variant="caption" sx={{ mt: 1 }}>
            {errors.profilePic}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12}>
        <Typography
          color="secondary"
          variant="caption"
          sx={{ mt: 1, fontStyle: "italic" }}
        >
          Fields marked * are required
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          fullWidth
          id="firstName"
          label="First Name"
          name="firstName"
          value={formData.firstName}
          onChange={handleInputChange}
          variant="outlined"
          disabled={isSubmitting}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          id="lastName"
          label="Last Name"
          name="lastName"
          value={formData.lastName}
          onChange={handleInputChange}
          variant="outlined"
          disabled={isSubmitting}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          id="emailAddress"
          label="Email Address"
          name="emailAddress"
          type="email"
          value={formData.emailAddress}
          onChange={handleInputChange}
          variant="outlined"
          disabled={isSubmitting}
          InputLabelProps={{ shrink: true }}
          placeholder="example@company.com"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          id="mobileNumber"
          label="Mobile Number"
          name="mobileNumber"
          type="tel"
          value={formData.mobileNumber}
          onChange={handleInputChange}
          variant="outlined"
          disabled={isSubmitting}
          InputLabelProps={{ shrink: true }}
          placeholder="234-567-8901"
          inputProps={{ maxLength: 10 }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          fullWidth
          select
          id="role"
          label="Role"
          name="role"
          value={formData.role}
          onChange={handleInputChange}
          variant="outlined"
          disabled={isSubmitting}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="" disabled>
            <em>Select a Role</em>
          </MenuItem>
          {userRoles &&
            userRoles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          fullWidth
          id="employeeCode"
          label="Username"
          name="employeeCode"
          value={formData.employeeCode}
          onChange={handleInputChange}
          variant="outlined"
          disabled={isSubmitting}
          InputLabelProps={{ shrink: true }}
        />
        <FormHelperText sx={{ display: "flex", alignItems: "center" }}>
          <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
          Cannot be more than 8 characters.
        </FormHelperText>
      </Grid>
    </>
  );
};

export default FormFields;
